/**
 * Detects that the company (tenant) was switched in ANOTHER browser window.
 *
 * All windows of one browser share a single server session, so picking a different
 * company in window B moves window A's session too, while A's top bar still shows the
 * old name. Two defences:
 *  - every API call carries the company this window was showing (X-Company-Id); the
 *    server answers 409 "company_changed" instead of acting on the wrong tenant;
 *  - when the window is brought back to the front we ask the server which company the
 *    session is on now. This also covers the classic screens embedded as iframes, whose
 *    requests do not go through the API client.
 * Either way `changedTo` is set and App.vue covers the page with a notice.
 */
import { reactive } from "vue";

export interface CompanyRef { id: number; name: string }

export const companyGuard = reactive<{
    /** The company this window was loaded for (from /menu). */
    expected: CompanyRef | null;
    /** Set once the session is known to be on a different company. */
    changedTo: CompanyRef | null;
}>({ expected: null, changedTo: null });

export function setExpectedCompany(c: CompanyRef | null): void {
    companyGuard.expected = c;
}

export function reportCompanyChanged(now: CompanyRef): void {
    if (!companyGuard.changedTo) companyGuard.changedTo = now;
}

let checking = false;

/** Ask the server which company the session is on and compare with this window. */
export async function checkCompanyNow(): Promise<void> {
    if (checking || !companyGuard.expected || companyGuard.changedTo) return;
    checking = true;
    try {
        const res = await fetch("/v2/app/api/menu", {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
        });
        if (!res.ok) return;
        const data = await res.json();
        const now = data?.company as CompanyRef | undefined;
        if (now && now.id !== companyGuard.expected.id) reportCompanyChanged(now);
    } catch { /* offline or logged out: the normal request path reports that */ }
    finally { checking = false; }
}

export function watchCompanyChanges(): void {
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void checkCompanyNow();
    });
    window.addEventListener("focus", () => { void checkCompanyNow(); });
}
